import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Navigate } from 'react-router-dom';
import { TafButton, TafCheckboxInput } from '@taf-ui/react';
import { TafChangeEvent } from '@taf-ui/core';
import cx from 'classnames';

import AppStorage from '../Storage/Storage';
import RouteParams from '../interfaces/RouteParams';
import { isBrand, translate } from '../utils/utils';
import DraftService from '../Requests/DraftService/DraftService';
import Steps from '../enums/Steps';
import Labels from '../enums/Labels';
import { Loader } from '../Loading/Loading';
import UnderwriterOwner from '../enums/UnderwriterOwner';
import HealthDeclarationStatus from '../enums/HealthDeclarationStatus';
import Brand from '../enums/Brand';

import './instructions.css';

import ReadSpeakerReader from '../ReadSpeakerReader/';
import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';

interface InstructionsState {
  navigateComponent?: ReactNode;
}

class Instructions extends React.PureComponent<RouteParams, InstructionsState> {
  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private readonly locationSearch: string | undefined;

  constructor(props: RouteParams) {
    super(props);
    this.locationSearch = props.locationSearch as string;
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();
    this.state = {
      navigateComponent: undefined,
    };
  }

  componentDidMount(): void {
    this.storage.modifyProgressBar(Steps.All, 'active', false);
    this.storage.modifyProgressBar(Steps.Instructions, 'active', true);
    setTimeout(() => {
      this.checkIfWeNeedToStoreDraft();
    }, 200);
  }

  componentWillUnmount(): void {
    this.storage.modifyProgressBar(Steps.Instructions, 'done', true);
    this.storage.modifyProgressBar(Steps.Instructions, 'active', false);
  }

  checkIfWeNeedToStoreDraft = (): void => {
    const currentHDStatus = this.storage.getCurrentHDStatus();
    /*
     * Added check that we store draft only for normal flow when declaration in status IN_PROGRESS
     * Because in some cases we can open this page in another status:
     * - wrong/unexpected redirect from signhost to /instructions route instead of /conclusion
     * - when user just closed signhost tab in browser and open link from email again (Status is WAITING_FOR_SIGHING),
     * but user would be redirected here. BE in this case will reset status to IN_PROGRESS because of posting new draft,
     * but we would be redirected to /waiting-for-signing that makes further signing just impossible.
     *
     * Also this check prevents resetting draft with default values for statuses different from IN_PROGRESS.
     * */
    if (currentHDStatus === HealthDeclarationStatus.IN_PROGRESS) {
      this.draftService.syncLater();
    }
  };

  goToNextStep = (): void => {
    const { healthDeclarationId } = this.props;

    this.draftService.saveDraft();
    this.setState({
      navigateComponent: (
        <Navigate
          to={`/${healthDeclarationId}/personal-details${this.locationSearch || ''}`}
          replace={true}
        />
      ),
    });
  };

  acceptInstructionsHandler = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsInstructionsAccepted(event.detail.value);
    this.storage.modifyProgressBar(
      Steps.Instructions,
      'valid',
      event.detail.value,
    );
    this.draftService.saveDraft();
  };

  getAgreeCheckboxLabel = () => {
    let label: Labels;

    if (isBrand(Brand.wefox)) {
      label = Labels.INSTRUCTIONS_CHECKBOX_TITLE_WEFOX;
    } else if (isBrand(Brand.DHMR)) {
      label = Labels.INSTRUCTIONS_CHECKBOX_TITLE_DHMR;
    } else {
      label =
        this.storage.getUnderwriterOwner === UnderwriterOwner.IPT_MAGNUM
          ? Labels.INSTRUCTIONS_CHECKBOX_TITLE_MAGNUM
          : Labels.INSTRUCTIONS_CHECKBOX_TITLE;
    }

    return translate(label, this.storage.getLocale);
  };

  getAgreeCheckbox = () => (
    <div className="checkbox-wrapper">
      <TafCheckboxInput
        value={this.storage.getIsInstructionsAccepted}
        name="isInstructionsAccepted"
        inputId="isInstructionsAccepted"
        label={this.getAgreeCheckboxLabel()}
        onTafChange={this.acceptInstructionsHandler}
        validationState={
          !this.storage.getIsInstructionsAccepted &&
          this.storage.getActiveStep?.done
            ? 'error'
            : undefined
        }
        validationMessage={translate(
          Labels.VALIDATE_INSTRUCTION_ACCEPTED_CHECKBOX,
          this.storage.getLocale,
        )}
      />
    </div>
  );

  getNextButton = () => (
    <TafButton
      disabled={!this.storage.getIsInstructionsAccepted}
      id="instructionsPageNextBtn"
      onClick={this.goToNextStep}
      type="button"
      className="proceed"
      primary={true}
      title={translate(Labels.NEXT_STEP, this.storage.getLocale)}
    >
      {translate(Labels.NEXT_STEP, this.storage.getLocale)}
    </TafButton>
  );

  render(): React.ReactElement {
    const lang = this.storage.getLocale;
    const productType = this.storage.getProductType();
    const productTypeName = translate(
      Labels[`PRODUCT_TYPE_${productType}` as keyof typeof Labels],
      lang,
    );
    const productSpecificInstructionsPage =
      this.storage.getProductSpecificInstructionsPage();
    const isWithoutHeader = isBrand(Brand.wefox);

    const { navigateComponent } = this.state;

    if (navigateComponent) {
      return <>{navigateComponent}</>;
    }

    if (!this.storage.getToken) {
      return <Loader />;
    }

    if (productSpecificInstructionsPage) {
      return (
        <div id="main-content" lang={languageToReadSpeakerLang(lang)}>
          <ReadSpeakerReader />
          <div
            className={cx('instructions', {
              instructionsWithoutHeader: isWithoutHeader,
            })}
            dangerouslySetInnerHTML={{
              __html: productSpecificInstructionsPage.replace(
                '{productType}',
                productTypeName,
              ),
            }}
          ></div>
          <div className="instructions-footer">
            <div className="column1"></div>
            <div className="column2">
              {this.getAgreeCheckbox()}
              {this.getNextButton()}
            </div>
          </div>
        </div>
      );
    }

    return <Loader />;
  }
}

export default observer(Instructions);
